import { useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

import { JAVA_DASHBOARD_API } from "../../../../../../constants";
import { handleDownload } from "../../../../../../utilityFunctions";

export default function useMonthlyReports() {

    const refDownloadFile = useRef<any>();

    const [reportData, setReportData] = useState<any>(null); // stores report data
    const [fileName, setFileName] = useState<string>(""); // stores the file name to be exported as csv
    const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false); // for download file loader


    const MONTHS = [
        { name: "January", id: 1 },
        { name: "February", id: 2 },
        { name: "March", id: 3 },
        { name: "April", id: 4 },
        { name: "May", id: 5 },
        { name: "June", id: 6 },
        { name: "July", id: 7 },
        { name: "August", id: 8 },
        { name: "September", id: 9 },
        { name: "October", id: 10 },
        { name: "November", id: 11 },
        { name: "December", id: 12 }
    ];

    const REPORTS = [
        { name: "Student App Usage", description: "", id: 1 },
        { name: "Subject wise Student App Usage", description: "", id: 2 },
        { name: "Grade wise Student App Usage", description: "", id: 3 },
        { name: "District wise Student App Usage", description: "", id: 4 }
    ];


    const [filters, setFilters] = useState<{
        months: { name: string, id: number }[];
        year: number;
    }>({
        months: [],
        year: 2025,
    });


    // HANDLER FUNCTIONS
    const handleReportDownload = (index: number) => {
        if (!filters.year) return;
        if (!filters.months.length) return;

        switch (index) {
            case 0:
                getMonthlyReportsForStudentAppUsage();
                break;
            case 1:
                getStudentAppUsageSubjectWise();
                break;
            case 2:
                getStudentAppUsageGradeWise();
                break;
            case 3:
                getStudentAppUsageDistrictWise();
                break;
            default:
                break;
        };
    };

    const generateReport = (data, labels, file_name) => {
        setFileName(file_name);

        // form data to download CSV
        let listData = data;
        let header = labels;
        // console.log("Data: ", listData, " Header: ", header);
        setReportData((oldData) => ({
            ...oldData,
            data: listData,
            headers: header,
        }));
        setIsDownloadingReport(false);

        setTimeout(() => {
            refDownloadFile?.current?.getElementsByTagName("a")[0]?.click();
        }, 1000);
    };


    // API CALLS
    const getMonthlyReportsForStudentAppUsage = async () => {

        setIsDownloadingReport(true);

        const QUERY_PARAMS = `?filters[0].year=${filters.year}${filters.months.map((each, index) => `&filters[0].months[${index}]=${each.name}`).join("")}`;
        const REQUEST_URL = `${JAVA_DASHBOARD_API}dashboard/monthly-reports/student-app-usage${QUERY_PARAMS}`;

        const response = handleDownload(
            REQUEST_URL,
            `Monthly-Report-For-Student-App-Usage_${moment().format("DD-MM-YYYY")}.csv`,
        ).finally(() => setIsDownloadingReport(false));

    };

    const getStudentAppUsageSubjectWise = async () => {

        setIsDownloadingReport(true);

        const QUERY_PARAMS = `?filters[0].year=${filters.year}${filters.months.map((each, index) => `&filters[0].months[${index}]=${each.name}`).join("")}`;
        const REQUEST_URL = `${JAVA_DASHBOARD_API}dashboard/monthly-reports/student-app-usage-subject-wise${QUERY_PARAMS}`;

        axios.get(REQUEST_URL).then(
            (response) => {
                // console.log("Response for student app usage subject wise: ", response?.data);

                let data = response.data || [];
                const keys = Object.keys(response.data[0]);
                let labels = keys.map(each => ({ label: each, key: each }))

                if (data.length && labels.length) {
                    generateReport(
                        data,
                        labels,
                        `Student-App-Usage-Subject-Wise_${moment().format("DD-MM-YYYY")}`
                    );
                };
            }, (error) => {
                console.log("Error caught: ", error?.response);
                toast.error("Failed to download report");
            }
        ).finally(() => setIsDownloadingReport(false));

    };

    const getStudentAppUsageGradeWise = async () => {

        setIsDownloadingReport(true);

        const QUERY_PARAMS = `?filters[0].year=${filters.year}${filters.months.map((each, index) => `&filters[0].months[${index}]=${each.name}`).join("")}`;
        const REQUEST_URL = `${JAVA_DASHBOARD_API}dashboard/monthly-reports/student-app-usage-grade-wise${QUERY_PARAMS}`;

        axios.get(REQUEST_URL).then(
            (response) => {
                // console.log("Response for student app usage subject wise: ", response?.data);

                let data = response.data || [];
                const keys = Object.keys(response.data[0]);
                let labels = keys.map(each => ({ label: each, key: each }))

                if (data.length && labels.length) {
                    generateReport(
                        data,
                        labels,
                        `Student-App-Usage-Grade-Wise_${moment().format("DD-MM-YYYY")}`
                    );
                };
            }, (error) => {
                console.log("Error caught: ", error?.response);
                toast.error("Failed to download report");
            }
        ).finally(() => setIsDownloadingReport(false));

    };

    const getStudentAppUsageDistrictWise = async () => {

        setIsDownloadingReport(true);

        const QUERY_PARAMS = `?filters[0].year=${filters.year}${filters.months.map((each, index) => `&filters[0].months[${index}]=${each.name}`).join("")}`;
        const REQUEST_URL = `${JAVA_DASHBOARD_API}dashboard/monthly-reports/student-app-usage-district-wise${QUERY_PARAMS}`;

        axios.get(REQUEST_URL).then(
            (response) => {
                // console.log("Response for student app usage district wise: ", response?.data);

                let formattedData = [];

                for (const district in response.data) {
                    let data = response.data[district]?.gradeWiseUsages?.map(
                        (each) => ({ ...each, "district": response.data[district]?.district })
                    ) || [];
                    formattedData.push(...data);
                };

                // console.log("Formatted data: ", formattedData);

                let data = formattedData;
                const keys = Object.keys(data[0]);
                let labels = keys.map(each => ({ label: each, key: each }))

                if (data.length && labels.length) {
                    generateReport(
                        data,
                        labels,
                        `Student-App-Usage-District-Wise_${moment().format("DD-MM-YYYY")}`
                    );
                };
            }, (error) => {
                console.log("Error caught: ", error?.response);
                toast.error("Failed to download report");
            }
        ).finally(() => setIsDownloadingReport(false));

    };


    return {
        MONTHS,
        REPORTS,
        filters,
        setFilters,
        refDownloadFile,
        reportData,
        fileName,
        isDownloadingReport,
        handleReportDownload
    };

};